import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, LogIn } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { products } from "@/data/products";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";

const Navbar = () => {
  const { itemCount, setIsOpen } = useCart();
  const { isAdmin, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin");

  const filteredProducts = searchQuery.length > 1
    ? products.filter((p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.style.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 5)
    : [];

  return (
    <>
      {/* Demo banner */}
      <div className="bg-primary px-4 py-1.5 text-center text-xs font-medium text-primary-foreground">
        ✨ Demo by <a href="https://hustlelabs.llc" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:opacity-80 transition-opacity">Hustle Labs</a> — explore freely, no real purchases!
      </div>

      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size={28} />
            <span className="font-display text-lg font-bold text-foreground md:text-xl">
              BraceletsByMia
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-6 md:flex">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Home
            </Link>
            <Link to="/products" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Shop All
            </Link>
            {isAdmin && (
              <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Dashboard
              </Link>
            )}
          </nav>

          {/* Desktop search + actions */}
          <div className="hidden items-center gap-3 md:flex">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search bracelets..."
                className="w-56 pl-9 text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchQuery) {
                    navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
                    setSearchQuery("");
                  }
                }}
              />
              {filteredProducts.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-card p-2 shadow-lg z-50">
                  {filteredProducts.map((p) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.id}`}
                      className="flex items-center gap-3 rounded-md p-2 hover:bg-secondary transition-colors"
                      onClick={() => setSearchQuery("")}
                    >
                      <div className="h-8 w-8 rounded" style={{ background: p.gradient }} />
                      <div>
                        <div className="text-sm font-medium text-foreground">{p.name}</div>
                        <div className="text-xs text-muted-foreground">${p.price}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {!isAdmin ? (
              <Link to="/admin/login">
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  <LogIn className="h-4 w-4 mr-1" />
                  Admin
                </Button>
              </Link>
            ) : (
              <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground">
                Logout
              </Button>
            )}

            {!isAdminRoute && (
              <Button
                variant="outline"
                size="sm"
                className="relative"
                onClick={() => setIsOpen(true)}
              >
                <ShoppingBag className="h-4 w-4" />
                {itemCount > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                    {itemCount}
                  </span>
                )}
              </Button>
            )}
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-2 md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(!searchOpen)}>
              <Search className="h-5 w-5" />
            </Button>
            {!isAdminRoute && (
              <Button variant="ghost" size="icon" className="relative" onClick={() => setIsOpen(true)}>
                <ShoppingBag className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                    {itemCount}
                  </span>
                )}
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile search */}
        {searchOpen && (
          <div className="border-t border-border p-3 md:hidden">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search bracelets..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchQuery) {
                    navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
                    setSearchQuery("");
                    setSearchOpen(false);
                  }
                }}
                autoFocus
              />
              {filteredProducts.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-card p-2 shadow-lg z-50">
                  {filteredProducts.map((p) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.id}`}
                      className="flex items-center gap-3 rounded-md p-2 hover:bg-secondary transition-colors"
                      onClick={() => { setSearchQuery(""); setSearchOpen(false); }}
                    >
                      <div className="h-8 w-8 rounded" style={{ background: p.gradient }} />
                      <div>
                        <div className="text-sm font-medium text-foreground">{p.name}</div>
                        <div className="text-xs text-muted-foreground">${p.price}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="border-t border-border p-4 md:hidden">
            <nav className="flex flex-col gap-3">
              <Link to="/" className="text-sm text-foreground" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/products" className="text-sm text-foreground" onClick={() => setMobileMenuOpen(false)}>Shop All</Link>
              {isAdmin ? (
                <>
                  <Link to="/admin" className="text-sm text-foreground" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                  <button className="text-left text-sm text-muted-foreground" onClick={() => { logout(); setMobileMenuOpen(false); }}>Logout</button>
                </>
              ) : (
                <Link to="/admin/login" className="text-sm text-foreground" onClick={() => setMobileMenuOpen(false)}>Admin Login</Link>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
